import { common } from "@shared/constants";
import { CURRENCY_MAP } from "@shared/constants/enums";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { getItemPrice } from "@shared/modules/financials/utils/price-utils";
import { useTranslation } from "@shared/utils/internationalization";
import { getTooltipPosition } from "@shared/utils/seating/tooltip-utils";
import CurrencyFormat from "@ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import Icon from "@ui-kit/icons/Icon.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { SeatingItemPropertyMap } from "../../types/seating-property-types";
import { MapTooltipProps } from "./MapTooltip.props";
import MapTooltipHeader from "./MapTooltipHeader/MapTooltipHeader.web";
import MapTooltipPurchaserDetails from "./MapTooltipHeader/MapTooltipPurchaserDetails";
import { useMapTooltip } from "./useMapTooltip";
import { MAP_TOOLTIP_WIDTH } from "./constants";

export default function MapTooltip({
  basketType,
  mousePosition,
  visible,
  info,
  allInPricing,
  canvasId,
  currency = CURRENCY_MAP.CAD.id,
  hidePrice = false,
  hideMinPurchaseText = false,
}: MapTooltipProps) {
  const { t } = useTranslation("seating");
  const { paymentType, purchaseSourcePlatform, itemGroups } =
    useBasketService();
  const {
    items,
    tooltipId,
    hasMoreItems,
    hasMoreCount,
    headerVals,
    purchaserDetails,
    headerLabel,
    statusLabel,
  } = useMapTooltip(info);
  const isBoxOffice = basketType === common.DataProvider.Venue;

  return (
    <Flex
      id={tooltipId}
      data-testid={tooltipId}
      hidden={!visible}
      zIndex="seatingMap"
      justifyContent="center"
      direction="column"
      pointerEvents="none"
      position="absolute"
      transform="translate(-50%, calc(-10px - 100%))"
      color="fg.inverted"
      backgroundColor="bg.inverted"
      padding="sm"
      borderRadius="sm"
      minWidth="fit-content"
      boxSizing="border-box"
      // Explicitly put these in an inline-style to avoid creating new classes on every coordinate
      // TODO: Use inline style prop for dynamic positioning
      // eslint-disable-next-line showpass-custom/prefer-semantic-props
      css={{
        top: getTooltipPosition(mousePosition, tooltipId, canvasId).top,
        left: getTooltipPosition(mousePosition, tooltipId, canvasId).left,
      }}
    >
      {/* Show segment-related text if it exists (ex: sold out status) */}
      {/* Else show item information for seats and GA locations */}
      <MapTooltipHeader headerVals={headerVals} />
      {purchaserDetails && (
        <MapTooltipPurchaserDetails
          email={purchaserDetails.email}
          nameOnTicket={purchaserDetails.name_on_ticket}
          phoneNumber={purchaserDetails.phone_number}
          headerLabel={headerLabel}
          statusLabel={statusLabel}
        />
      )}
      <Flex
        hidden={!info?.itemTypes || info?.itemTypes?.length === 0}
        direction="column"
        textAlign="left"
        borderTop="1px solid"
        borderColor="border"
      >
        {items.map((item: SeatingItemPropertyMap, index: number) => {
          const showDivider =
            index !== info.itemTypes.length - 1 ||
            info.isAccessible ||
            info.isLimitedVisibility;

          const pwycPrice = itemGroups.find(
            (ig) => ig.type === item.id
          )?.pay_what_you_can_price;

          return (
            <Flex
              key={item.id}
              alignItems="center"
              mt={index === 0 ? "var(--chakra-spacing-2xs)" : "none"}
              mb={showDivider ? "var(--chakra-spacing-2xs)" : "none"}
              pb={showDivider ? "var(--chakra-spacing-2xs)" : "none"}
              borderBottom={showDivider ? "1px solid" : "none"}
              borderBottomColor={
                showDivider ? "var(--chakra-colors-border)" : "none"
              }
            >
              <Text
                width="element.xs"
                height="element.xs"
                borderRadius="50%"
                marginRight="xs"
                style={{
                  background: item.colorFill,
                }}
                color="fg.inverted"
              />
              <Box>
                {info.isResale && <Text>{t("Showpass Official Resale")}</Text>}
                <Text
                  maxWidth={MAP_TOOLTIP_WIDTH}
                  isTruncated
                  color="fg.inverted"
                >
                  {item.name}
                </Text>
                {!hidePrice && (
                  <Text fontWeight="bold" color="fg.inverted">
                    <CurrencyFormat
                      value={getItemPrice(
                        pwycPrice ?? item?.price?.toString(),
                        pwycPrice ? false : allInPricing,
                        item?.fees_pricing_info,
                        paymentType,
                        purchaseSourcePlatform
                      )}
                      displayType="text"
                      currency={currency}
                    />
                  </Text>
                )}
                {!hideMinPurchaseText &&
                  item.minimum_purchase_limit &&
                  item.minimum_purchase_limit > 1 && (
                    <Text color="secondary.400">
                      {/* TODO: fix desktop translations */}
                      {isBoxOffice
                        ? `Minimum ${item.minimum_purchase_limit} tickets required`
                        : t("Minimum {{count}} tickets required", {
                            count: item.minimum_purchase_limit,
                          })}
                    </Text>
                  )}
              </Box>
            </Flex>
          );
        })}
      </Flex>
      <Flex
        justifyContent="space-around"
        hidden={!info.isAccessible && !info.isLimitedVisibility}
        mb={
          hasMoreItems
            ? "var(--chakra-spacing-2xs)"
            : "var(--chakra-spacing-none)"
        }
        pb={
          hasMoreItems
            ? "var(--chakra-spacing-2xs)"
            : "var(--chakra-spacing-none)"
        }
        borderBottom={hasMoreItems ? "1px solid" : "none"}
        borderBottomColor={
          hasMoreItems ? "var(--chakra-colors-border)" : "none"
        }
      >
        {info.isLimitedVisibility && (
          <Flex py="none" px="2xs" alignItems="center">
            <Icon.Info color="fg.inverted" boxSize="element.lg" mr="2xs" />
            <Text color="fg.inverted">{t("Limited Visibility")}</Text>
          </Flex>
        )}
        {info.isAccessible && (
          <Flex py="none" px="2xs" alignItems="center">
            <Icon.Info color="fg.inverted" boxSize="element.lg" mr="2xs" />
            <Text color="fg.inverted">{t("Accessible")}</Text>
          </Flex>
        )}
      </Flex>
      {hasMoreItems && (
        <Flex py="none" px="2xs" alignItems="center" justifyContent="center">
          <Text color="fg.inverted">
            {t(`Click to see {{count}} more items`, {
              count: hasMoreCount,
            })}
          </Text>
        </Flex>
      )}
    </Flex>
  );
}
